import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { 
  Crown, Check, X, Sparkles, Percent, Zap, Star, Gift,
  CreditCard, ArrowRight, Shield, Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const PLANS = [
  {
    id: 'free',
    name: 'Gratuit',
    price: 0,
    commission: 20,
    color: 'gray',
    features: [
      { text: 'Accès à toutes les recettes', included: true },
      { text: 'Participer aux défis', included: true },
      { text: 'Créer des stories', included: true },
      { text: 'Recevoir des tips', included: true },
      { text: '20% de commission', included: false },
      { text: 'Badge Premium', included: false },
      { text: 'Support prioritaire', included: false },
    ]
  },
  {
    id: 'premium',
    name: 'Premium',
    price: 20,
    commission: 15,
    color: 'blue',
    popular: true,
    features: [
      { text: 'Tout le plan Gratuit', included: true },
      { text: '15% de commission seulement', included: true },
      { text: 'Badge Premium', included: true },
      { text: 'Priorité dans les défis', included: true },
      { text: 'Statistiques avancées', included: true },
      { text: 'Support prioritaire', included: false },
      { text: 'Accès anticipé nouvelles features', included: false },
    ]
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 50,
    commission: 10,
    color: 'yellow',
    features: [
      { text: 'Tout le plan Premium', included: true },
      { text: '10% de commission seulement', included: true },
      { text: 'Badge Pro exclusif', included: true },
      { text: 'Support prioritaire 24/7', included: true },
      { text: 'Accès anticipé nouvelles features', included: true },
      { text: 'Mise en avant de tes recettes', included: true },
      { text: 'Invitations événements exclusifs', included: true },
    ]
  }
];

export default function SubscriptionPage() {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [currentPlan, setCurrentPlan] = useState('free');

  useEffect(() => {
    if (user) {
      setCurrentPlan(user.creator_tier || 'free');
    }
  }, [user]);

  const handleSubscribe = async (planId) => {
    if (planId === 'free') {
      toast.info('Tu es déjà sur le plan gratuit');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/stripe/create-subscription-session', {
        plan: planId
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Erreur de configuration Stripe');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la souscription');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-6" data-testid="subscription-page">
      {/* Header */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1E4ED8]/10 rounded-full mb-4">
          <Sparkles className="w-4 h-4 text-[#1E4ED8]" />
          <span className="text-sm font-medium text-[#1E4ED8]">Abonnements Créateur</span>
        </div>
        <h1 className="font-fraunces text-3xl font-bold text-gray-900 mb-3">
          Maximise tes revenus
        </h1>
        <p className="text-gray-500 max-w-xl mx-auto">
          Choisis le plan qui correspond à ton activité et garde plus de tes gains.
        </p>
      </div>

      {/* Current Plan Badge */}
      {currentPlan !== 'free' && (
        <div className="mb-8 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Crown className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="font-medium text-green-900">Tu es actuellement sur le plan {currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)}</p>
              <p className="text-sm text-green-700">Commission réduite à {currentPlan === 'premium' ? '15' : '10'}%</p>
            </div>
          </div>
          <Check className="w-6 h-6 text-green-500" />
        </div>
      )}

      {/* Plans Grid */}
      <div className="grid md:grid-cols-3 gap-6">
        {PLANS.map((plan) => (
          <div 
            key={plan.id}
            className={`relative bg-white rounded-2xl border-2 overflow-hidden transition-all hover:shadow-lg ${
              plan.popular ? 'border-[#1E4ED8] shadow-lg shadow-[#1E4ED8]/10' : 'border-gray-100'
            } ${currentPlan === plan.id ? 'ring-2 ring-green-500' : ''}`}
          >
            {/* Popular Badge */}
            {plan.popular && (
              <div className="absolute top-0 right-0 bg-[#1E4ED8] text-white text-xs font-medium px-3 py-1 rounded-bl-lg">
                Populaire
              </div>
            )}

            {/* Current Plan Badge */}
            {currentPlan === plan.id && (
              <div className="absolute top-0 left-0 bg-green-500 text-white text-xs font-medium px-3 py-1 rounded-br-lg">
                Plan actuel
              </div>
            )}

            <div className="p-6">
              {/* Plan Header */}
              <div className="mb-6">
                <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-3 ${
                  plan.color === 'yellow' ? 'bg-yellow-100 text-yellow-700' :
                  plan.color === 'blue' ? 'bg-blue-100 text-blue-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  <Crown className="w-4 h-4" />
                  <span className="font-medium text-sm">{plan.name}</span>
                </div>
                
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                  <span className="text-gray-500">TND/mois</span>
                </div>
                
                <div className="flex items-center gap-2 mt-2">
                  <Percent className="w-4 h-4 text-[#1E4ED8]" />
                  <span className="text-sm text-gray-600">
                    Commission: <span className="font-semibold">{plan.commission}%</span>
                  </span>
                </div>
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    {feature.included ? (
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                    ) : (
                      <X className="w-5 h-5 text-gray-300 flex-shrink-0" />
                    )}
                    <span className={feature.included ? 'text-gray-700' : 'text-gray-400'}>
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <Button
                onClick={() => handleSubscribe(plan.id)}
                disabled={loading || currentPlan === plan.id}
                className={`w-full ${
                  plan.popular 
                    ? 'bg-[#1E4ED8] hover:bg-[#1842B8]' 
                    : plan.id === 'pro'
                    ? 'bg-yellow-500 hover:bg-yellow-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {currentPlan === plan.id ? (
                  'Plan actuel'
                ) : plan.id === 'free' ? (
                  'Plan de base'
                ) : (
                  <>
                    Choisir {plan.name}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Benefits Section */}
      <div className="mt-12 bg-gradient-to-br from-[#F8F5F2] to-white rounded-2xl p-8">
        <h3 className="font-fraunces text-xl font-bold text-gray-900 mb-6 text-center">
          Pourquoi passer Premium?
        </h3>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-[#1E4ED8]/10 flex items-center justify-center mx-auto mb-3">
              <Percent className="w-6 h-6 text-[#1E4ED8]" />
            </div>
            <h4 className="font-semibold text-gray-900 mb-1">Moins de commissions</h4>
            <p className="text-sm text-gray-500">Garde jusqu'à 90% de tes revenus avec le plan Pro</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-3">
              <Star className="w-6 h-6 text-yellow-500" />
            </div>
            <h4 className="font-semibold text-gray-900 mb-1">Plus de visibilité</h4>
            <p className="text-sm text-gray-500">Tes recettes et défis sont mis en avant</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
              <Shield className="w-6 h-6 text-green-500" />
            </div>
            <h4 className="font-semibold text-gray-900 mb-1">Support prioritaire</h4>
            <p className="text-sm text-gray-500">Accède à une aide dédiée 24/7</p>
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div className="mt-12">
        <h3 className="font-fraunces text-xl font-bold text-gray-900 mb-6 text-center">
          Questions fréquentes
        </h3>
        <div className="space-y-4 max-w-2xl mx-auto">
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <h4 className="font-medium text-gray-900 mb-2">Puis-je annuler à tout moment?</h4>
            <p className="text-sm text-gray-500">Oui, tu peux annuler ton abonnement à tout moment. Tu garderas les avantages jusqu'à la fin de la période payée.</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <h4 className="font-medium text-gray-900 mb-2">Comment fonctionne la facturation?</h4>
            <p className="text-sm text-gray-500">Tu seras facturé mensuellement. Le paiement est sécurisé par Stripe.</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <h4 className="font-medium text-gray-900 mb-2">Puis-je changer de plan?</h4>
            <p className="text-sm text-gray-500">Oui, tu peux upgrader ou downgrader ton plan à tout moment. La différence sera calculée au prorata.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
